import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Shift } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { addWeeks } from 'date-fns/addWeeks'
import { format } from 'date-fns/format'
import { isSameDay } from 'date-fns/isSameDay'
import { isToday } from 'date-fns/isToday'
import { subWeeks } from 'date-fns/subWeeks'
import { useNavigate } from 'react-router'

import ArrowLeft from '@/assets/icons/icon-chevron-left.svg'
import ArrowRight from '@/assets/icons/icon-chevron-right.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button'
import { EmptySlot } from '@/components/ui/empty/empty-provider'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table/table'
import type { Creator } from '@/modules/creators/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers'

import type {
  DeleteShiftRequestOptions,
  ShiftGroupsMapByCreators,
} from '../types'
import { getUniqueShiftKey } from '../types'
import { ShiftView } from '../ui/shift'
import { ShiftsTableSkeleton } from '../ui/table-skeleton'
import {
  formatLocally,
  getDayFromMonday,
  getWeekDays,
  highlightColumn,
} from '../utils'
import { getColorKey, getColorMap } from './utils'

interface Props {
  shiftGroupsMapByCreators?: ShiftGroupsMapByCreators
  allCreators: Creator[]
  filteredCreators: Creator[]
  isLoading: boolean
  onClickEdit: (shift: Shift) => void
  onClickDelete: (options: DeleteShiftRequestOptions) => Promise<void>
  areActionsVisible: (shift: Shift) => boolean
  filterShiftByPeople: (shift: Shift) => boolean
  setMonth: (date: Date) => void
  date: Date
}

const formatDate = (date: string) => formatLocally(date, 'yyyy-MM-dd')

export interface ShiftsCalendarRef {
  jumpToWeek: (week: Date) => void
  pulseDay: (week: Date) => void
}

export const ShiftsCalendar = memo(
  forwardRef<ShiftsCalendarRef, Props>(
    (
      {
        shiftGroupsMapByCreators,
        allCreators,
        filteredCreators,
        onClickEdit,
        onClickDelete,
        isLoading,
        areActionsVisible,
        filterShiftByPeople,
        setMonth,
        date,
      },
      ref,
    ) => {
      const [week, setWeek] = useState(date)
      const tableRef = useRef<HTMLTableElement | null>(null)
      const navigate = useNavigate()

      const handleClickAddCreator = () => {
        navigate(AppRoutes.Creators.List.asUrl())
      }

      const handleSetWeek = useCallback(
        (week: Date) => {
          setWeek(week)
          setMonth(getWeekDays(week)[0])
        },
        [setMonth, setWeek],
      )

      useImperativeHandle(
        ref,
        () => ({
          jumpToWeek,
          pulseDay,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
      )

      const jumpToWeek = (week: Date) => {
        handleSetWeek(week)
      }

      const pulseDay = (date: Date) => {
        // +1 for the first column with creator name
        const index =
          getWeekDays(date).findIndex((day) => isSameDay(day, date)) + 1

        if (tableRef.current) {
          highlightColumn(tableRef.current, index)
        }
      }

      const shiftGroupsByDate = useMemo(
        () =>
          Object.fromEntries(
            Object.entries(shiftGroupsMapByCreators ?? {}).map(
              ([id, shifts]) => {
                const shiftsMap = shifts
                  .filter(filterShiftByPeople)
                  .sort(
                    (a, b) =>
                      new Date(a.startedAt).getTime() -
                      new Date(b.startedAt).getTime(),
                  )
                  .reduce(
                    (acc, shift) => {
                      const date = formatDate(shift.startedAt)

                      acc[date] = (acc[date] || []).concat(shift)

                      return acc
                    },
                    {} as Record<string, Shift[]>,
                  )

                return [id, shiftsMap]
              },
            ),
          ),
        [shiftGroupsMapByCreators, filterShiftByPeople],
      )

      const colorsMap = useMemo(() => {
        if (!shiftGroupsByDate) return

        const shifts = getWeekDays(week).flatMap((day) =>
          Object.values(shiftGroupsByDate)
            .flatMap((shiftsMap) => shiftsMap[formatDate(day.toISOString())])
            .filter(Boolean),
        )

        return getColorMap(shifts)
      }, [week, shiftGroupsByDate])

      const recurringUntilEnd = useCallback(
        (shift: Shift) => {
          if (!shift.repeatableShiftId) return []

          return (
            shiftGroupsMapByCreators?.[shift.creatorId]?.reduce<Shift[]>(
              (acc, currentShift) => {
                if (
                  currentShift.repeatableShiftId === shift.repeatableShiftId &&
                  new Date(currentShift.startedAt).getTime() >
                    new Date(shift.startedAt).getTime()
                ) {
                  acc.push(currentShift)
                }

                return acc
              },
              [],
            ) ?? []
          )
        },
        [shiftGroupsMapByCreators],
      )

      const renderCell = useCallback(
        (creator: Creator, shifts?: Shift[]) => {
          if (!shifts || !colorsMap) return <div />

          return (
            <div className="flex flex-col items-start space-y-1">
              {shifts.map((shift) => (
                <ShiftView
                  shift={shift}
                  color={colorsMap[getColorKey(shift)]}
                  key={getUniqueShiftKey(shift)}
                  onClickEdit={onClickEdit}
                  onClickDelete={onClickDelete}
                  recurringsUntilEnd={recurringUntilEnd(shift)}
                  creator={creator}
                  areActionsVisible={areActionsVisible(shift)}
                />
              ))}
            </div>
          )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [recurringUntilEnd, colorsMap],
      )

      const calendarColumns: ColumnDef<Creator>[] = useMemo(
        () =>
          getWeekDays(week).map((date) => ({
            accessorKey: weekdays[getDayFromMonday(date)],
            header: () => (
              <div className="space-y-[2px]">
                <div
                  className={classNames('leading-6 dark:text-text-default', {
                    'text-primary-500 dark:text-primary-500': isToday(date),
                  })}
                >
                  {weekdays[getDayFromMonday(date)].slice(0, 3)}
                </div>
                <div
                  className={classNames('text-xl font-semibold text-title', {
                    'text-primary-500 dark:text-primary-500': isToday(date),
                  })}
                >
                  {date.getDate()}
                </div>
              </div>
            ),
            cell: ({ row: { original: creator } }) => {
              const formattedDate = format(date, 'yyyy-MM-dd')
              const shifts = shiftGroupsByDate[creator.id]?.[formattedDate]

              return renderCell(creator, shifts)
            },
          })),
        [week, shiftGroupsByDate, renderCell],
      )

      const columns: ColumnDef<Creator>[] = useMemo(
        () => [
          {
            accessorKey: 'arrow-left',
            header: () => (
              <Button
                variant="outline"
                className="h-9 p-2"
                onClick={() => handleSetWeek(subWeeks(week, 1))}
              >
                <ArrowLeft
                  type="button"
                  height={20}
                  width={20}
                />
              </Button>
            ),
            cell: ({ row: { original: creator } }) => {
              return (
                <div className="space-y-1">
                  <CreatorAvatar
                    key={creator?.id}
                    className="h-8 w-8"
                    name={creator.name}
                    avatarSrc={creator.avatar.croppedUrl}
                  />
                  <div className="text-xs leading-5">{creator.name}</div>
                </div>
              )
            },
          },
          ...calendarColumns,
          {
            accessorKey: 'arrow-right',
            header: () => (
              <Button
                variant="outline"
                className="h-9 p-2"
                onClick={() => handleSetWeek(addWeeks(week, 1))}
              >
                <ArrowRight
                  type="button"
                  height={20}
                  width={20}
                />
              </Button>
            ),
          },
        ],
        [calendarColumns, week, handleSetWeek],
      )

      const table = useReactTable({
        data: filteredCreators,
        columns,
        getCoreRowModel: getCoreRowModel(),
      })

      const headerGroups = table.getHeaderGroups()

      const renderHeader = useMemo(() => {
        return headerGroups.map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className={classNames(
                  'border-x border-x-transparent px-2 py-4 transition-colors duration-300',
                  {
                    'w-[104px] px-6': header.id.includes('arrow'),
                  },
                )}
              >
                <div
                  className={classNames('flex items-center', {
                    'justify-end': header.id === 'arrow-right',
                  })}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        ))
      }, [headerGroups])

      const { rows } = table.getRowModel()

      const renderRows = useMemo(
        () => {
          if (rows.length === 0) {
            const creatorsDoesntExist = (
              <>
                <div className="mb-[6px] text-2xl font-semibold">
                  You don’t have any creators added to the account yet
                </div>
                <div className="mb-4 text-base text-secondary-400">
                  Add a creator to start creating shifts
                </div>
                <Button
                  onClick={handleClickAddCreator}
                  className="gap-1"
                >
                  <PlusIcon />
                  <span className="inline">Add Creator</span>
                </Button>
              </>
            )
            const noSelectedCreators = (
              <div className="text-2xl font-semibold">
                No creators have been selected
              </div>
            )
            const emptyContent =
              allCreators.length === 0
                ? creatorsDoesntExist
                : noSelectedCreators

            return (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <EmptySlot>
                    {() => (
                      <EmptyTableContent
                        imageProps={{
                          height: 170,
                          width: 170,
                          className: '-mt-4',
                        }}
                      >
                        {emptyContent}
                      </EmptyTableContent>
                    )}
                  </EmptySlot>
                </TableCell>
              </TableRow>
            )
          }

          return rows.map((row) => (
            <TableRow
              key={row.id}
              className="[&_td:last-child]:border-0 [&_td:nth-last-child(2)]:border-0 [&_td]:border-r"
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <TableCell
                    key={cell.id}
                    className={classNames(
                      'p-2 align-top transition-colors duration-300',
                      {
                        'px-6': cell.id.includes('arrow-left'),
                      },
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              })}
            </TableRow>
          ))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, columns, shiftGroupsByDate],
      )

      return isLoading ? (
        <ShiftsTableSkeleton />
      ) : (
        <div className="flex w-full flex-col overflow-auto">
          <div className="rounded-2xl bg-card">
            <Table
              ref={tableRef}
              className="min-w-[780px] table-fixed"
            >
              <TableHeader className="opacity-100">{renderHeader}</TableHeader>
              <TableBody>{renderRows}</TableBody>
            </Table>
          </div>
        </div>
      )
    },
  ),
)

ShiftsCalendar.displayName = 'ShiftsCalendar'
